import { ReactNode } from 'react';
import LayoutWrapper from '../../layouts/wrapper-layout';
import { JobPosition } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';
import { NextSeoProps } from 'next-seo';
import { BASE_URL, defaultSEO } from '@restworld/utils-common';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { getSalary } from '../../hooks/use-salary';

export const JobPositionLayout = ({
  children,
  jobPositionStatic,
}: {
  children: ReactNode;
  jobPositionStatic?: Partial<JobPosition>;
}) => {
  const { isAuthenticated, user } = useAuth();
  const router = useRouter();
  const { t } = useTranslation('common');
  let seoProps: NextSeoProps | undefined = undefined;
  if (jobPositionStatic) {
    const occupation = jobPositionStatic?.occupation?.label_it?.label;
    const title = jobPositionStatic?.job_title || occupation;
    const fallbackDescription = t('job_position_seo_fallback_description', { occupation });
    const description = buildJobPositionDescription(jobPositionStatic, t) || fallbackDescription;
    const isActive = ['jp_screening', 'jp_shortlist_formation', 'jp_shortlist_sent'].includes(
      jobPositionStatic?.status_key
    );
    const metaTitle = jobPositionStatic?.meta_title || title;
    const metaDescription = jobPositionStatic?.meta_description || description;
    const images = [];
    if (jobPositionStatic?.meta_image_url) {
      images.push({
        url: jobPositionStatic.meta_image_url,
        width: 1200,
        height: 630,
        alt: metaTitle,
      });
    }
    const currentUrl = `${BASE_URL}${router.asPath}`;
    const canonicalUrl = jobPositionStatic?.semantic_unique_name
      ? `${BASE_URL}/posizione/${jobPositionStatic.semantic_unique_name}`
      : currentUrl;
    seoProps = {
      title: metaTitle,
      description: metaDescription,
      noindex: !isActive,
      canonical: canonicalUrl,
      openGraph: {
        siteName: defaultSEO.title,
        url: canonicalUrl,
        title: metaTitle,
        description: metaDescription,
        type: 'article',
        images: images.length > 0 ? images : undefined,
      },
    };
  }

  return (
    <LayoutWrapper
      hasFooter
      hasNavbar
      showSearchBarInNavbar={isAuthenticated ? user?.type !== 'employer' : true}
      // Bare title: the layout appends the brand suffix.
      title={jobPositionStatic?.job_title ?? 'Offerta lavorativa'}
      seoProps={seoProps}
      // Title, description and the ad itself come from the employer and have no
      // English variant: only the chrome around them is translated. Same
      // treatment as the venue showcase.
      languageSeoMode="consolidated"
      testId="job-position-page"
    >
      {children}
    </LayoutWrapper>
  );
};
export default JobPositionLayout;

const buildJobLocationLabel = (jp: Partial<JobPosition>): string => {
  const r = jp.restaurant;
  if (!r) return '';
  if (jp.show_restaurant_details && r.restaurant_name) {
    return r.locality ? `da ${r.restaurant_name} a ${r.locality}` : `da ${r.restaurant_name}`;
  }
  return r.locality ? `a ${r.locality}` : '';
};

const buildContractLabel = (jp: Partial<JobPosition>): string => {
  const contract = jp.contract_type_var?.label;
  const jobType = jp.job_type_var?.label;
  return [contract, jobType].filter(Boolean).join(' ');
};

const hasSalary = (jp: Partial<JobPosition>): boolean => Boolean(jp.salary || jp.min_salary || jp.max_salary);

export const buildJobPositionDescription = (jp: Partial<JobPosition>, t: (key: string) => string): string => {
  const role = jp.job_title || jp.occupation?.label_it?.label;
  if (!role) return '';
  const location = buildJobLocationLabel(jp);
  const head = [role, location].filter(Boolean).join(' ');
  const salary = hasSalary(jp) ? getSalary({ jobPositionApiData: jp, t }) : '';
  const parts = [head, buildContractLabel(jp), salary].filter(Boolean);
  return `${parts.join(' · ')} · Candidati su Restworld.`;
};
